'use client';

import * as Slider from '@radix-ui/react-slider';
import type { ChangeEvent, FormEvent } from 'react';
import { useMemo, useState } from 'react';
import NumberInput from '../../inputs/NumberInput';

type Props<Attribute> = {
  min: number;
  max: number;
  minLabel: string;
  maxLabel: string;
  values?: number[];
  attribute: Attribute;
  onChange: (
    event: ChangeEvent<HTMLInputElement>,
    attribute: Attribute,
  ) => void;
};

export default function Range<Attribute>(props: Props<Attribute>) {
  const { min, max, minLabel, maxLabel, onChange, attribute, values } = props;

  const [minValue, setMinValue] = useState(values?.[0] ?? min);
  const [maxValue, setMaxValue] = useState(values?.[1] ?? max);

  const displayValue = useMemo(() => {
    if (values === undefined) return [minValue, maxValue];
    setMinValue(values[0]);
    setMaxValue(values[1]);
    return values;
  }, [values, minValue, maxValue]);

  const handleSubmit = (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    const data = new FormData(event.target as HTMLFormElement);
    const minValue = Number(data.get('minValue'));
    const maxValue = Number(data.get('maxValue'));
    const [actualMin, actualMax] = [minValue, maxValue].sort((a, b) => a - b);
    const newValue = {
      target: {
        type: 'range',
        min: actualMin,
        max: actualMax,
      },
    } as unknown;
    setMinValue(actualMin);
    setMaxValue(actualMax);
    onChange(newValue as ChangeEvent<HTMLInputElement>, attribute);
  };

  const handleChange = ([minValue, maxValue]: [number, number]) => {
    const newValue = {
      target: {
        type: 'range',
        min: minValue,
        max: maxValue,
      },
    } as unknown;
    setMinValue(minValue);
    setMaxValue(maxValue);
    onChange(newValue as ChangeEvent<HTMLInputElement>, attribute);
  };

  return (
    <div
      key={JSON.stringify({
        ...displayValue,
        min,
        max,
      })}>
      <Slider.Root
        min={min}
        max={max}
        minStepsBetweenThumbs={1}
        className="relative flex h-4 w-full touch-none select-none items-center py-3"
        defaultValue={displayValue}
        onValueChange={([minValue, maxValue]) => {
          setMinValue(minValue);
          setMaxValue(maxValue);
        }}
        onValueCommit={handleChange}>
        <Slider.Track className="relative h-px w-full grow rounded-full bg-black" />
        <Slider.Thumb className="block h-3 w-3 rounded-full bg-black" />
        <Slider.Thumb className="block h-3 w-3 rounded-full bg-black" />
      </Slider.Root>
      <form
        onSubmit={handleSubmit}
        className="mt-2 grid grid-cols-8 gap-x-2 xl:gap-x-4">
        <div className="grid gap-y-2 overflow-hidden max-md:col-span-3 md:col-span-4 2xl:col-span-3">
          <NumberInput
            labelClassName="uppercase text-grey-300"
            id="minValue"
            min={min}
            max={max}
            name="minValue"
            label={minLabel}
            alignLabel="left"
            value={Math.round(minValue)}
          />
        </div>
        <div className="grid gap-y-2 overflow-hidden max-md:col-span-3 max-md:col-start-6 md:col-span-4 2xl:col-span-3 2xl:col-start-6">
          <NumberInput
            labelClassName="uppercase text-grey-300"
            id="maxValue"
            min={min}
            max={max}
            name="maxValue"
            label={maxLabel}
            alignLabel="right"
            value={Math.round(maxValue)}
          />
        </div>
        <button type="submit" className="invisible" aria-label="Submit" />
      </form>
    </div>
  );
}
